/**
 * API client — minimal fetch wrapper.
 *
 *   - Adds Bearer token from localStorage on every request
 *   - Parses JSON, throws ApiError on non-2xx
 *   - Returns parsed body for convenience
 *
 * Used by all data hooks. No external dependency on axios etc.
 */

const TOKEN_KEY = 'behold_token';

export class ApiError extends Error {
    constructor(message, status, body) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

export const tokenStore = {
    get:   () => localStorage.getItem(TOKEN_KEY),
    set:   (t) => localStorage.setItem(TOKEN_KEY, t),
    clear: ()  => localStorage.removeItem(TOKEN_KEY),
};

async function request(method, path, body) {
    const headers = { 'Content-Type': 'application/json' };
    const token = tokenStore.get();
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(`/api${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    let payload = null;
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        payload = await res.json().catch(() => null);
    } else {
        payload = await res.text().catch(() => null);
    }

    if (!res.ok) {
        // 401 → token is invalid; force re-login
        if (res.status === 401) {
            tokenStore.clear();
            // Soft signal: AuthContext picks this up via the storage event
            window.dispatchEvent(new Event('behold:auth-expired'));
        }
        const msg = payload?.error || payload?.message || res.statusText;
        throw new ApiError(msg, res.status, payload);
    }

    return payload;
}

export const api = {
    get:    (path)         => request('GET',    path),
    post:   (path, body)   => request('POST',   path, body),
    put:    (path, body)   => request('PUT',    path, body),
    patch:  (path, body)   => request('PATCH',  path, body),
    delete: (path)         => request('DELETE', path),
};

/* ----------------------------------------------------------------------------
 * Convenience: query string builder
 * ---------------------------------------------------------------------------- */
export function qs(params) {
    const filtered = Object.entries(params || {})
        .filter(([, v]) => v !== undefined && v !== null && v !== '');
    if (filtered.length === 0) return '';
    return '?' + new URLSearchParams(filtered).toString();
}
